// Base CRM Integration Class
// Provides common functionality for all CRM integrations

import { BaseIntegration } from './BaseIntegration';
import {
  IntegrationConfig,
  SyncOperation,
  DataConflict,
  FieldMapping,
  SyncDirection
} from '../../../types/integration';

// CRM-specific types
export interface CRMContact {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  company?: string;
  phone?: string;
  customFields: Record<string, any>;
  createdAt: Date;
  updatedAt: Date;
  lastActivityAt?: Date;
}

export interface CRMActivity {
  id: string;
  contactId: string;
  type: 'email_open' | 'email_click' | 'email_bounce' | 'email_unsubscribe' | 'form_submit';
  timestamp: Date;
  details: Record<string, any>;
  campaignId?: string;
  emailId?: string;
}

export interface CRMSyncOptions {
  direction: SyncDirection;
  batchSize: number;
  includeActivities: boolean;
  fieldMappings: FieldMapping[];
  conflictResolution: 'manual' | 'email_wins' | 'crm_wins';
  dateRange?: {
    start: Date;
    end: Date;
  };
}

export interface CRMSyncResult {
  operation: SyncOperation;
  contactsProcessed: number;
  contactsSucceeded: number;
  contactsFailed: number;
  activitiesProcessed: number;
  activitiesSucceeded: number;
  activitiesFailed: number;
  conflicts: DataConflict[];
  errors: any[];
}

export abstract class BaseCRMIntegration extends BaseIntegration {
  
  constructor(config: IntegrationConfig) {
    super(config);
  }

  // Abstract methods specific to CRM integrations
  abstract syncContacts(options: CRMSyncOptions): Promise<CRMSyncResult>;
  abstract syncActivities(activities: CRMActivity[]): Promise<void>;
  abstract getContact(contactId: string): Promise<CRMContact | null>;
  abstract createContact(contact: Partial<CRMContact>): Promise<CRMContact>;
  abstract updateContact(contactId: string, updates: Partial<CRMContact>): Promise<CRMContact>;
  abstract deleteContact(contactId: string): Promise<void>;
  abstract searchContacts(query: string, limit?: number): Promise<CRMContact[]>;
  abstract getCustomFields(): Promise<FieldMapping[]>;

  // Common CRM functionality

  /**
   * Sync email engagement activities to CRM
   */
  async logEmailActivity(activity: CRMActivity): Promise<void> {
    try {
      await this.executeWithRetry(async () => {
        await this.syncActivities([activity]);
      });

      await this.emitEvent('activity.logged', { activity });
    } catch (error) {
      await this.emitEvent('activity.log_failed', { 
        activity, 
        error: error instanceof Error ? error.message : 'Unknown error' 
      });
      throw error;
    }
  }

  /**
   * Batch sync multiple email activities
   */
  async logEmailActivities(activities: CRMActivity[]): Promise<void> {
    const batchSize = this.config.settings.batchSize || 100;
    
    for (let i = 0; i < activities.length; i += batchSize) {
      const batch = activities.slice(i, i + batchSize);
      
      try {
        await this.executeWithRetry(async () => {
          await this.syncActivities(batch);
        });

        await this.emitEvent('activities.batch_logged', { 
          batchSize: batch.length,
          totalProcessed: i + batch.length,
          totalActivities: activities.length
        });
      } catch (error) {
        await this.emitEvent('activities.batch_failed', { 
          batch, 
          error: error instanceof Error ? error.message : 'Unknown error' 
        });
        
        // Continue with next batch even if one fails
        console.error(`Failed to sync activity batch ${i}-${i + batch.length}:`, error);
      }
    }
  }

  /**
   * Sync contact from email platform to CRM
   */
  async syncContactToCRM(emailContact: any): Promise<CRMContact> {
    try {
      // Transform email contact to CRM format
      const crmContact = await this.transformEmailContactToCRM(emailContact);
      
      // Check if contact already exists
      const existingContact = await this.findContactByEmail(crmContact.email);
      
      if (existingContact) {
        // Update existing contact
        const updatedContact = await this.updateContact(existingContact.id, crmContact);
        
        await this.emitEvent('contact.updated', { 
          contactId: existingContact.id,
          changes: crmContact
        });
        
        return updatedContact;
      } else {
        // Create new contact
        const newContact = await this.createContact(crmContact);
        
        await this.emitEvent('contact.created', { 
          contactId: newContact.id,
          contact: newContact
        });
        
        return newContact;
      }
    } catch (error) {
      await this.emitEvent('contact.sync_failed', { 
        emailContact, 
        error: error instanceof Error ? error.message : 'Unknown error' 
      });
      throw error;
    }
  }

  /**
   * Sync contact from CRM to email platform
   */
  async syncContactFromCRM(crmContactId: string): Promise<any> {
    try {
      const crmContact = await this.getContact(crmContactId);
      if (!crmContact) {
        throw new Error(`CRM contact ${crmContactId} not found`);
      }

      // Transform CRM contact to email platform format
      const emailContact = await this.transformCRMContactToEmail(crmContact);
      
      // Update in email platform (this would call the email platform's API)
      const updatedEmailContact = await this.updateEmailPlatformContact(emailContact);
      
      await this.emitEvent('contact.synced_from_crm', { 
        crmContactId,
        emailContactId: updatedEmailContact.id
      });
      
      return updatedEmailContact;
    } catch (error) {
      await this.emitEvent('contact.sync_from_crm_failed', { 
        crmContactId, 
        error: error instanceof Error ? error.message : 'Unknown error' 
      });
      throw error;
    }
  }

  /**
   * Handle webhook from CRM platform
   */
  async handleCRMWebhook(payload: any): Promise<void> {
    try {
      const eventType = this.extractWebhookEventType(payload);
      
      switch (eventType) {
        case 'contact.created':
        case 'contact.updated':
          await this.handleContactWebhook(payload);
          break;
        case 'contact.deleted':
          await this.handleContactDeletedWebhook(payload);
          break;
        default:
          console.log(`Unhandled CRM webhook event: ${eventType}`);
      }
      
      await this.emitEvent('webhook.processed', { eventType, payload });
    } catch (error) {
      await this.emitEvent('webhook.processing_failed', { 
        payload, 
        error: error instanceof Error ? error.message : 'Unknown error' 
      });
      throw error;
    }
  }

  // Protected helper methods

  protected async findContactByEmail(email: string): Promise<CRMContact | null> {
    try {
      const contacts = await this.searchContacts(email, 1);
      return contacts.length > 0 ? contacts[0] : null;
    } catch (error) {
      console.error('Error finding contact by email:', error);
      return null;
    }
  }

  protected async transformEmailContactToCRM(emailContact: any): Promise<Partial<CRMContact>> {
    // Apply field mappings to transform email contact to CRM format
    const fieldMappings = this.config.fieldMappings.filter(
      mapping => mapping.direction === 'to_platform' || mapping.direction === 'bidirectional'
    );

    const crmContact: Partial<CRMContact> = {
      email: emailContact.email,
      customFields: {}
    };

    for (const mapping of fieldMappings) {
      const sourceValue = this.getNestedValue(emailContact, mapping.sourceField);
      if (sourceValue !== undefined) {
        const transformedValue = await this.applyTransformation(sourceValue, mapping.transformation);
        this.setNestedValue(crmContact, mapping.targetField, transformedValue);
      }
    }

    return crmContact;
  }

  protected async transformCRMContactToEmail(crmContact: CRMContact): Promise<any> {
    // Apply field mappings to transform CRM contact to email platform format
    const fieldMappings = this.config.fieldMappings.filter(
      mapping => mapping.direction === 'from_platform' || mapping.direction === 'bidirectional'
    );

    const emailContact: any = {
      email: crmContact.email
    };

    for (const mapping of fieldMappings) {
      const sourceValue = this.getNestedValue(crmContact, mapping.sourceField);
      if (sourceValue !== undefined) {
        const transformedValue = await this.applyTransformation(sourceValue, mapping.transformation);
        this.setNestedValue(emailContact, mapping.targetField, transformedValue);
      }
    }

    return emailContact;
  }

  protected async applyTransformation(value: any, transformation?: any): Promise<any> {
    if (!transformation) return value;

    // Apply transformation logic based on transformation type
    switch (transformation.type) {
      case 'format':
        return this.formatValue(value, transformation.config);
      case 'calculate':
        return this.calculateValue(value, transformation.config);
      case 'lookup':
        return this.lookupValue(value, transformation.config);
      case 'custom':
        return this.executeCustomTransformation(value, transformation.script);
      default:
        return value;
    }
  }

  protected formatValue(value: any, _config: any): any {
    // Implement value formatting logic
    return value;
  }

  protected calculateValue(value: any, _config: any): any {
    // Implement value calculation logic
    return value;
  }

  protected lookupValue(value: any, _config: any): any {
    // Implement value lookup logic
    return value;
  }

  protected executeCustomTransformation(value: any, _script: string): any {
    // Implement custom transformation script execution
    return value;
  }

  protected getNestedValue(obj: any, path: string): any {
    return path.split('.').reduce((current, key) => current?.[key], obj);
  }

  protected setNestedValue(obj: any, path: string, value: any): void {
    const keys = path.split('.');
    const lastKey = keys.pop()!;
    const target = keys.reduce((current, key) => {
      if (!current[key]) current[key] = {};
      return current[key];
    }, obj);
    target[lastKey] = value;
  }

  protected abstract extractWebhookEventType(payload: any): string;
  protected abstract handleContactWebhook(payload: any): Promise<void>;
  protected abstract handleContactDeletedWebhook(payload: any): Promise<void>;
  protected abstract updateEmailPlatformContact(contact: any): Promise<any>;
}